import React, { useEffect, useState } from "react";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { HexColorPicker, HexColorInput } from "react-colorful";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import ReactSelect from "react-select";
import MenuItem from "@mui/material/MenuItem";

import { MoveUp, Search } from "lucide-react";
import { MoveDown } from "lucide-react";
import { Card, CardContent } from "@mui/material";

import Button from "@mui/material/Button";
import { TextField, Switch, Typography } from "@mui/material";
import { axiosInstance } from "../../utility/apiInstance";
import { ApiResponse, FromDataType, MilestoneGroup } from "./grouping-milestone";
import toast from "react-hot-toast";
import { useRole } from "../../components/MangeContextProvide";
import SearchInput from "../../common/SearchInput";

// ── Themed react-select styles (matches app's amber/blue pill design) ──────
const reactSelectStyles: any = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 44,
    borderRadius: 6,
    backgroundColor: "#f9f9f9",
    borderColor: state.isFocused ? "#ffb310" : "#ddd",
    boxShadow: state.isFocused ? "0 0 0 1px #ffb310" : "none",
    "&:hover": { borderColor: "#ffb310" },
  }),
  valueContainer: (base: any) => ({
    ...base,
    flexWrap: "wrap",
    rowGap: "6px",
    padding: "6px 10px",
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: "#e8f4fd",
    borderRadius: "6px",
    overflow: "hidden",
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: "#1565c0",
    fontWeight: 600,
    fontSize: "0.78rem",
    padding: "4px 6px",
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: "#1565c0",
    ":hover": { backgroundColor: "#c8e2fb", color: "#0d47a1" },
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    alignSelf: "stretch",
  }),
  clearIndicator: (base: any) => ({
    ...base,
    color: "#aaa",
    padding: 8,
    cursor: "pointer",
    ":hover": { color: "#d32f2f" },
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "#aaa",
    padding: 8,
    cursor: "pointer",
    ":hover": { color: "#ffb310" },
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    backgroundColor: "#e0e0e0",
    marginTop: 8,
    marginBottom: 8,
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 6,
    overflow: "hidden",
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: "0.9rem",
    backgroundColor: state.isFocused ? "#fff4de" : "#fff",
    color: "#333",
    "&:active": { backgroundColor: "#ffe6ad" },
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#999",
    fontSize: "0.9rem",
  }),
};

// Keeps the table area a stable height across loading, populated, and
// empty states so the page doesn't jump/collapse as data changes.
const MIN_TABLE_HEIGHT = 360;

function GroupMilestones() {
  const [openGroup, setOpenGroup] = React.useState(false);
  const [openMilestone, setOpenMilestone] = React.useState(false);
  const [firstMilestone, setFirstMilestone] = useState<string[]>([]);
  const [lastMilestone, setLastMilestone] = React.useState([]);
  const [milestoneData, setMilestoneData] = useState<MilestoneGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const { globleCustomerId } = useRole() as any;



  const [color, setColor] = useState("#156082");
  const [showPicker, setShowPicker] = useState(false);
  const [milestone, setMilestone] = useState("first");
  const [type, setType] = useState("Air");

  const [formData, setFromData] = useState<FromDataType>({
    groupName: '',
    colorCode: "#156082",
    description: "",
    type: "Air"
  })


  const [manualMilestones, setManualMilestones] = useState({
    airfirstMilestones: [],
    airlastMilestones: [],
    oceanfirstMilestones: [],
    oceanlastMilestones: [],
  });
  const [errors, setErrors] = useState({
    groupName: "",
    description: "",
    milestones: "",
    type: ""
  });
  const firstOptions = [
    { value: 10, label: "Air" },
    { value: 20, label: "Ocean" },
  ];




  // fetch milestones
  const fetchMilestones = async (search: string) => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get<ApiResponse>(
        `api/v1/groupingMileStone?search=${search}`
      );
      setMilestoneData(data.data);
    } catch (err: any) {
      toast.error("Failed to fetch milestones.");
    } finally {
      setLoading(false);
    }
  };

  // debounce search effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);


  useEffect(() => {
    fetchMilestones(debouncedSearch);
  }, [debouncedSearch]);

  const handleChangeSwitch = async (event: React.ChangeEvent<HTMLInputElement>, milestone: MilestoneGroup) => {
    try {
      const { data } = await axiosInstance.put(`api/v1/groupingMileStone/${milestone._id}`, {
        isActive: !milestone.isActive
      })
      toast.success(data.message);
      fetchMilestones(debouncedSearch);
    } catch (error) {
      toast.error("something went wrong")
    }

  };



  const validateGroupForm = () => {
    let newErrors = { groupName: "", description: "", milestones: "", type: "" };
    if (!formData.groupName.trim()) newErrors.groupName = "Group name is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";

    setErrors(newErrors);
    return !Object.values(newErrors).some((err) => err);
  };
  const validateForm = () => {

    let newErrors = { groupName: "", description: "", milestones: "", type: "" };
    if (!formData.groupName.trim()) newErrors.groupName = "Group name is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";
    if (!formData.type.trim())
      newErrors.type = "type is required";
    if (firstMilestone.length === 0 && lastMilestone.length === 0)
      newErrors.milestones = "At least one milestone must be selected";

    setErrors(newErrors);
    return !Object.values(newErrors).some((err) => err);
  };


  const handleEdit = (id: string, milestone: any) => {
    setOpenGroup(true)
    setEditingId(id);
    setFirstMilestone(milestone?.firstmilestones.map((item: any) => ({ label: item.name, value: item })) || [])
    setLastMilestone(milestone?.lastmilestones.map((item: any) => ({ label: item.name, value: item })) || [])
    setFromData((prev) => ({
      ...prev,
      groupName: milestone.groupName,
      description: milestone.description,
      type: milestone.type,
      colorCode: milestone.colorCode,

    }));
    if (milestone.type == "air") {
      setType("Air")
    } else if (milestone.type == "ocean") {
      setType("Ocean")
    }
  };


  const handleMilestone = async (miletype: string) => {

    if (miletype === "Air") {
      let firstMilestoneRes = await axiosInstance.get(
        "/api/v1/awb-manual-milestone",
        {
          params: { type: "first" },
        }
      );
      setManualMilestones((prev) => ({
        ...prev,
        airfirstMilestones: firstMilestoneRes?.data?.data || [],
      }));

      let lastMilestoneRes = await axiosInstance.get(
        "/api/v1/awb-manual-milestone",
        {
          params: { type: "last" },
        }
      );
      setManualMilestones((prev) => ({
        ...prev,
        airlastMilestones: lastMilestoneRes?.data?.data,
      }));
    } else if (miletype === "Ocean") {
      let firstMilestoneRes = await axiosInstance.get(
        "/api/v1/ocean-manual-milestone",
        {
          params: { type: "first" },
        }
      );
      setManualMilestones((prev) => ({
        ...prev,
        oceanfirstMilestones: firstMilestoneRes?.data?.data || [],
      }));

      let lastMilestoneRes = await axiosInstance.get(
        "/api/v1/ocean-manual-milestone",
        {
          params: { type: "last" },
        }
      );
      setManualMilestones((prev) => ({
        ...prev,
        oceanlastMilestones: lastMilestoneRes?.data?.data,
      }));
    }
  };

  const handleType = (type: string) => {

    setType(type);
    setFromData((prev) => (
      {
        ...prev,
        type: type
      }
    ));
    setFirstMilestone([]);
    setLastMilestone([]);
  };

  useEffect(() => {
    handleMilestone("Air");
    handleMilestone("Ocean");
  }, []);



  const handleChangeMileStone = (event: any) => {
    const value = event.target.value;

    if (milestone == "first") {
      const unique = value.filter(
        (v: any, i: number, self: any[]) =>
          i === self.findIndex((t) => t._id === v._id)
      );
      setFirstMilestone(unique);
    } else if (milestone == "last") {
      const unique = value.filter(
        (v: any, i: number, self: any[]) =>
          i === self.findIndex((t) => t._id === v._id)
      );
      setLastMilestone(unique);
    }


  };


  const moveItem = (index: number, direction: "up" | "down") => {

    if (milestone == "first") {
      const newItems = [...firstMilestone];
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= newItems.length) return;
      [newItems[index], newItems[targetIndex]] = [
        newItems[targetIndex],
        newItems[index],
      ];
      setFirstMilestone(newItems);
    } else if (milestone == "last") {
      const newItems = [...lastMilestone];
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= newItems.length) return;
      [newItems[index], newItems[targetIndex]] = [
        newItems[targetIndex],
        newItems[index],
      ];
      setLastMilestone(newItems);
    }

  };


  const handleChangeGroup = (e: any, name: string) => {
    let value = e.target.value;
    if (name === "groupName") value = value.replace(/[^a-zA-Z0-9\s]/g, "");

    setFromData((prev) => ({ ...prev, [name]: value }));
  };

  async function handleAddGrouping() {

    if (!validateForm()) return;
    try {

      let payload = {
        colorCode: color || formData.colorCode,
        groupName: formData.groupName,
        description: formData.description,
        firstmilestones: firstMilestone.map((el: any) => el.value._id.toString()),
        lastmilestones: lastMilestone.map((el: any) => el.value._id.toString()),
        type: formData.type.toLowerCase()
      };

      let apiUrl = "/api/v1/groupingMileStone";
      if (editingId != null) {
        apiUrl = `/api/v1/groupingMileStone/updatestatus/${editingId}`
      }
      const { data } = await axiosInstance.post(
        apiUrl,
        payload
      );

      fetchMilestones("");
      toast.success(data.message);

      setFromData({
        groupName: '',
        colorCode: "#156082",
        description: "",
        type: "Air"
      });
      setFirstMilestone([]);
      setLastMilestone([]);
      setOpenMilestone(false);
      setOpenGroup(false);
      setMilestone("first");
      setEditingId(null);
      setType("Air");
    } catch (err: any) {
      console.error(err, "888")
      let error = err?.response?.data?.error || "Server error."
      toast.error(error)

    }
  }



  return (
    <>
      <div className="w-full">
        <div className="old_dashboard_title">
          <h1 className="text-2xl font-bold text-gray-700 mt-[-15px] mb-[10px]">
            Manage Milestone Grouping
          </h1>
        </div>
      </div>

      <div className="BoxWhite">
        <div className="milestoneMain">
          <div className="mSearchButton">
            <button
              className="btnTheme btnPadding"
              onClick={() => setOpenGroup(true)}
            >
              Add Grouping
            </button>
            <Dialog
              open={openMilestone}
              onClose={() => setOpenMilestone(false)}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle className="headingModalCustom">Add Grouping Milestone</DialogTitle>
              <DialogContent>

                <div className="col-span-12 lg:col-span-12 md:col-span-12">
                  <div className="mangeInput">
                    <label className="colorLable">
                      Select Air/Ocean
                    </label>
                    <FormControl>
                      <Select
                        disabled={editingId != null}
                        displayEmpty
                        value={type}
                        onChange={(e) => handleType(e.target.value)}
                      >

                        {firstOptions.map((opt) => (
                          <MenuItem
                            key={opt.value}
                            value={opt.label}
                          >
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>

                      {errors.type && (
                        <Typography color="error" variant="caption">
                          {errors.type}
                        </Typography>
                      )}
                    </FormControl>
                  </div>
                </div>
                <div className="BoxWhite">
                  <div className="milestonetabss">
                    {/* Tabs header */}
                    <div
                      style={{
                        display: "flex",
                        gap: "16px",
                        borderBottom: "1px solid rgb(220 205 160)",
                      }}
                    >
                      <button
                        onClick={() => setMilestone("first")}
                        className={`milestone-btn ${milestone === "first" ? "activee" : ""
                          }`}
                      >
                        First Mile
                      </button>

                      <button
                        onClick={() => setMilestone("last")}
                        className={`milestone-btn ${milestone === "last" ? "activee" : ""
                          }`}
                      >
                        Last Mile
                      </button>
                    </div>

                    {/* Tab content */}
                    <div style={{ marginTop: "16px" }}>
                      {milestone === "first" && (
                        <div className="commonTCMilestone mt-6 space-y-6">
                          <div className="grid grid-cols-12 mt-5  gap-4">
                            
                            <div className="col-span-12 md:col-span-12 lg:col-span-12">
                              <div className="mangeInput">
                                <label className="colorLable">
                                  Select Milestone
                                </label>

                                <ReactSelect<any, true>
                                  isMulti
                                  styles={reactSelectStyles}
                                  name="firstmilestone"
                                  options={type == "Air" ? manualMilestones.airfirstMilestones.map((item: any) => ({
                                    label: item.name,
                                    value: item,
                                  })) : manualMilestones.oceanfirstMilestones.map((item: any) => ({
                                    label: item.name,
                                    value: item,
                                  }))
                                  }
                                  value={firstMilestone}
                                  onChange={(options: any) => {
                                    // remove duplicates by value
                                    const unique = options.filter(
                                      (v: any, i: number, a: any[]) =>
                                        a.findIndex((t: any) => t.value._id === v.value._id) === i
                                    );
                                    setFirstMilestone(unique);
                                  }}
                                />
                                {errors.milestones && (
                                  <Typography color="error" variant="caption">
                                    {errors.milestones}
                                  </Typography>
                                )}

                              </div>
                            </div>
                            {/* </div> */}
                          </div>
                        </div>
                      )}
                      {milestone === "last" && (
                        <div className="commonTCMilestone">
                          <div className="grid grid-cols-12 mt-5 gap-3">
                            
                            <div className="col-span-12 md:col-span-12 lg:col-span-12">
                              <div className="mangeInput">
                                <label className="colorLable">
                                  Select Milestone
                                </label>
                                <FormControl sx={{ m: 0, width: "100%" }}>


                                  <ReactSelect<any, true>
                                    isMulti
                                    styles={reactSelectStyles}
                                    name="lastmilestone"
                                    options={type == "Air" ? manualMilestones.airlastMilestones.map((item: any) => ({
                                      label: item.name,
                                      value: item,
                                    })) : manualMilestones.oceanlastMilestones.map((item: any) => ({
                                      label: item.name,
                                      value: item,
                                    }))}
                                    value={lastMilestone}
                                    onChange={(options: any) => {
                                      const unique = options.filter(
                                        (v: any, i: number, a: any[]) =>
                                          a.findIndex((t: any) => t.value._id === v.value._id) === i
                                      );
                                      setLastMilestone(unique);
                                    }}

                                  />
                                  {errors.milestones && (
                                    <Typography color="error" variant="caption">
                                      {errors.milestones}
                                    </Typography>
                                  )}
                                </FormControl>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>


                  {/* up-down  */}
                  {
                    milestone == "first" ? (
                      <div
                        className="switchmilstone mt-6"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 10,
                        }}
                      >
                        {type == "Air"
                          ? firstMilestone.map((item: any, index) => (
                            <Card
                              key={item.value._id}
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                backgroundColor: "#fffaf0",
                                borderColor: "#f0d896",
                                transition: "box-shadow 0.15s ease",
                                "&:hover": { boxShadow: "0 2px 8px rgba(255,179,16,0.25)" },
                              }}
                            >
                              <CardContent
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  py: "10px !important",
                                  px: 2,
                                }}
                              >
                                {/* Center: label */}
                                <Typography sx={{ fontWeight: 500, color: "#4a3f35", fontSize: "0.92rem" }}>
                                  {item.value.name}
                                </Typography>

                                {/* Right side: move arrows */}
                                <div className="arrowmilstone" style={{ display: "flex", gap: 2 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveItem(index, "up")}
                                    disabled={index === 0}
                                    sx={{ color: "#b8860b" }}
                                  >
                                    <MoveUp size={18} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveItem(index, "down")}
                                    disabled={index === firstMilestone.length - 1}
                                    sx={{ color: "#b8860b" }}
                                  >
                                    <MoveDown size={18} />
                                  </IconButton>
                                </div>
                              </CardContent>
                            </Card>
                          ))
                          : firstMilestone.map(
                            (item: any, index) => (
                              <Card
                                key={item.value._id}
                                variant="outlined"
                                sx={{
                                  borderRadius: 2,
                                  backgroundColor: "#fffaf0",
                                  borderColor: "#f0d896",
                                  transition: "box-shadow 0.15s ease",
                                  "&:hover": { boxShadow: "0 2px 8px rgba(255,179,16,0.25)" },
                                }}
                              >
                                <CardContent
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: "10px !important",
                                    px: 2,
                                  }}
                                >
                                  {/* Center: label */}
                                  <Typography sx={{ fontWeight: 500, color: "#4a3f35", fontSize: "0.92rem" }}>
                                    {item.value.name}
                                  </Typography>

                                  {/* Right side: move arrows */}
                                  <div className="arrowmilstone" style={{ display: "flex", gap: 2 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => moveItem(index, "up")}
                                      disabled={index === 0}
                                      sx={{ color: "#b8860b" }}
                                    >
                                      <MoveUp size={18} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => moveItem(index, "down")}
                                      disabled={index === firstMilestone.length - 1}
                                      sx={{ color: "#b8860b" }}
                                    >
                                      <MoveDown size={18} />
                                    </IconButton>
                                  </div>
                                </CardContent>
                              </Card>
                            )
                          )}
                      </div>
                    ) : (
                      <div
                        className="switchmilstone mt-6"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 10,
                        }}
                      >
                        {type == "Air"
                          ? lastMilestone.map((item: any, index) => (
                            <Card
                              key={item.value._id}
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                backgroundColor: "#fffaf0",
                                borderColor: "#f0d896",
                                transition: "box-shadow 0.15s ease",
                                "&:hover": { boxShadow: "0 2px 8px rgba(255,179,16,0.25)" },
                              }}
                            >
                              <CardContent
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  py: "10px !important",
                                  px: 2,
                                }}
                              >
                                {/* Center: label */}
                                <Typography sx={{ fontWeight: 500, color: "#4a3f35", fontSize: "0.92rem" }}>
                                  {item.value.name}
                                </Typography>

                                {/* Right side: move arrows */}
                                <div className="arrowmilstone" style={{ display: "flex", gap: 2 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveItem(index, "up")}
                                    disabled={index === 0}
                                    sx={{ color: "#b8860b" }}
                                  >
                                    <MoveUp size={18} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveItem(index, "down")}
                                    disabled={index === lastMilestone.length - 1}
                                    sx={{ color: "#b8860b" }}
                                  >
                                    <MoveDown size={18} />
                                  </IconButton>
                                </div>
                              </CardContent>
                            </Card>
                          ))
                          : lastMilestone.map(
                            (item: any, index) => (
                              <Card
                                key={item.value._id}
                                variant="outlined"
                                sx={{
                                  borderRadius: 2,
                                  backgroundColor: "#fffaf0",
                                  borderColor: "#f0d896",
                                  transition: "box-shadow 0.15s ease",
                                  "&:hover": { boxShadow: "0 2px 8px rgba(255,179,16,0.25)" },
                                }}
                              >
                                <CardContent
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: "10px !important",
                                    px: 2,
                                  }}
                                >
                                  {/* Center: label */}
                                  <Typography sx={{ fontWeight: 500, color: "#4a3f35", fontSize: "0.92rem" }}>
                                    {item.value.name}
                                  </Typography>

                                  {/* Right side: move arrows */}
                                  <div className="arrowmilstone" style={{ display: "flex", gap: 2 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => moveItem(index, "up")}
                                      disabled={index === 0}
                                      sx={{ color: "#b8860b" }}
                                    >
                                      <MoveUp size={18} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => moveItem(index, "down")}
                                      disabled={index === lastMilestone.length - 1}
                                      sx={{ color: "#b8860b" }}
                                    >
                                      <MoveDown size={18} />
                                    </IconButton>
                                  </div>
                                </CardContent>
                              </Card>
                            )
                          )}
                      </div>
                    )
                  }

                </div>
              </DialogContent>
              <DialogActions>
                <Button className="btn btnBlack_22" onClick={() => {
                  setOpenMilestone(false)
                  // setFromData((prev) => ({ ...prev, groupName: "", description: "" }));
                  setFirstMilestone([]);
                  setLastMilestone([]);
                  // setEditingId(null);
                  setType("Air");
                  setErrors({ groupName: "", description: "", milestones: "", type: "" });
                }}>Cancel</Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="btn btnYellow_22"
                  onClick={handleAddGrouping}
                >
                  {editingId != null ? "Update" : "Save"}
                </Button>
              </DialogActions>
            </Dialog>

            <div className="mSearchGroup inline-block align-top relative">
              <SearchInput
                placeholder="Search Grouping"
                onChange={setSearchQuery}
                width={400}
              />
            </div>
          </div>

          <Dialog
            open={openGroup}
            onClose={() => setOpenGroup(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogContent>
              <div className="addGroupForm pt-3">
                <div className="grid grid-cols-12 mt-1 gap-3 mb-1">
                  {/* Group Name */}
                  <div className="col-span-6">
                    <label className="colorLable">Group Name</label>
                    <TextField
                      // label="Group Name"
                      placeholder=""
                      variant="outlined"
                      className="w-full"
                      sx={{
                        "& .MuiInputBase-input": {
                          height: "43px",
                          padding: "0 8px",
                          fontSize: "15px",
                        },
                        "& .MuiInputLabel-root": {
                          top: "-5px",
                          fontSize: "15px",
                        },
                        "& .MuiInputLabel-shrink": {
                          top: "0px",
                        },
                      }}
                      value={formData.groupName}
                      onChange={(e) => handleChangeGroup(e, "groupName")}
                      error={!!errors.groupName}
                      helperText={errors.groupName}
                    />
                  </div>

                  {/* Color Picker */}
                  <div className="col-span-6">
                    <label className="colorLable">Select Color </label>
                    <div
                      className="colorbox"
                      style={{ position: "relative", width: "100%" }}
                    >
                      <div
                        onClick={() => setShowPicker(true)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          position: "relative",
                          cursor: "pointer",
                          padding: "5px",
                          border: "1px solid #ddd",
                          borderRadius: "3px",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            height: "31px",
                            backgroundColor: color,
                            padding: "0px",
                          }}
                        />
                        <span
                          style={{
                            fontFamily: "monospace",
                            color: "white",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "37px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            pointerEvents: "none",
                          }}
                        >
                          {color}
                        </span>
                      </div>

                      {showPicker && (
                        <div
                          style={{
                            marginTop: "10px",
                            width: "100%",
                            padding: "8px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            paddingLeft: "15px",
                            paddingRight: "15px",
                            position: "relative",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                          }}
                        >
                          {/* Close Icon */}
                          <IconButton
                            size="small"
                            onClick={() => setShowPicker(false)}
                            style={{
                              position: "absolute",
                              top: -6,
                              right: -11,
                              background: "rgba(0,0,0,0.5)",
                              color: "white",
                              zIndex: 999,
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>

                          <HexColorPicker
                            color={color}
                            onChange={setColor}
                            style={{ width: "100%" }}
                          />
                          <HexColorInput
                            color={color}
                            onChange={setColor}
                            prefixed
                            style={{
                              width: "100%",
                              padding: "6px 8px",
                              border: "1px solid #ccc",
                              borderRadius: "4px",
                              fontSize: "14px",
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="col-span-12">
                    <label className="colorLable">Description</label>
                    <TextField
                      label=""
                      className="w-full"
                      variant="outlined"
                      value={formData.description}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "90px",
                          alignItems: "flex-start",
                        },
                        "& textarea": {
                          padding: "10px 14px",
                          boxSizing: "border-box",
                        },
                      }}
                      onChange={(e) => handleChangeGroup(e, "description")}
                      error={!!errors.description}
                      helperText={errors.description}

                    />
                  </div>

                  <div className="col-span-12 flex justify-end gap-3 mt-3">
                    <Button
                      className="btnTheme btnPadding"
                      color="secondary"
                      onClick={() => {
                        setOpenGroup(false)
                        setFromData((prev) => ({ ...prev, groupName: "", description: "" }));
                        setFirstMilestone([]);
                        setLastMilestone([]);
                        setEditingId(null);
                        setType("Air");
                        setErrors({ groupName: "", description: "", milestones: "", type: "" });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="btnTheme btnPadding"
                      // disabled={!validateForm}
                      onClick={() => {
                        let response = validateGroupForm();
                        if (response) {
                          setOpenMilestone(true)
                        }
                      }}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <div className="mileTable mt-6">
            <TableContainer sx={{ minHeight: MIN_TABLE_HEIGHT }}>
              <Table className="tableMilestone">
                <TableHead>
                  <TableRow className="uppercase">
                    <TableCell>S.No </TableCell>
                    <TableCell>Group Name </TableCell>
                    <TableCell>Hex Color</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Milestones</TableCell>
                    <TableCell>Added on</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ height: MIN_TABLE_HEIGHT, border: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            gap: 1.5,
                          }}
                        >
                          <CircularProgress size={32} thickness={4} />
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            Loading groupings...
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : milestoneData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ height: MIN_TABLE_HEIGHT, border: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            No groupings found
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    milestoneData.map((milestone: any, index: number) => {
                      const rowBorder = { borderBottom: "1px solid rgba(224, 224, 224, 1) !important" };
                      return (
                    <TableRow key={milestone._id || index}>
                      <TableCell sx={rowBorder}>{index + 1}</TableCell>
                      <TableCell sx={rowBorder}>{milestone.groupName}</TableCell>
                      <TableCell sx={rowBorder}>
                        <span className="w-[90px] h-[30px] bg-yellow-600 block text-white p-1 text-center rounded-md">
                          {milestone.colorCode}
                        </span>
                      </TableCell>
                      <TableCell sx={rowBorder}>{milestone.description}</TableCell>
                      <TableCell sx={rowBorder}>{milestone.milestones}</TableCell>
                      <TableCell sx={rowBorder}>{milestone.addedOn} </TableCell>

                      <TableCell align="center" sx={rowBorder}>
                        <Switch
                          className="thinSwitch"
                          checked={milestone.isActive}
                          disabled={globleCustomerId != milestone.customer}
                          onChange={(e) => handleChangeSwitch(e, milestone)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell sx={rowBorder}>
                        <button
                          className="btn btnYellow tableMilestone"
                          disabled={!milestone.isActive || globleCustomerId != milestone.customer} 
                          onClick={() => handleEdit(milestone?._id || null, milestone)}
                          // variant="outlined"
                        >
                          Edit
                        </button>
                      </TableCell>
                    </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </div>
    </>
  );
}

export default GroupMilestones;